"use client"

import { useState } from "react"
import { Head, useForm, router } from "@inertiajs/react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { toast } from 'sonner'
import { Save, ArrowLeft } from "lucide-react"
import DashboardLayout from "@/layout/dashboard-layout"

interface Blog {
  id: number
  name: string
  domain: string
  active: boolean
}

interface Category {
  id: number
  name: string
}

interface Post {
  id: number
  title: string
  content: string
  status: string
  scheduled_at?: string
  blog_id: number
  category_id: number
  url?: string
  published_in?: string
}

interface Props {
  post: Post
  blogs: Blog[]
  categories: Category[]
  errors?: string[]
}

export default function EditarPostagemPage({ post, blogs, categories, errors }: Props) {
  const { data, setData, post: postData, processing } = useForm({
    title: post.title || '',
    content: post.content || '',
    status: post.status || 'draft',
    scheduled_at: post.scheduled_at || '',
    blog_id: post.blog_id || '',
    category_id: post.category_id || '',
    url: post.url || '',
    published_in: post.published_in || '',
    blog_ids: post.blog_id ? [post.blog_id] : [],
    _method: 'patch'
  })

  const [selectedBlogs, setSelectedBlogs] = useState<number[]>(post.blog_id ? [post.blog_id] : [])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    // Atualizar os dados do form com blog_ids
    setData('blog_ids', selectedBlogs)
    
    postData(`/dashboard/posts/${post.id}/update_post`, {
      onSuccess: () => {
        toast.success('Post atualizado com sucesso!')
      },
      onError: (errors: any) => {
        console.error('Erro ao atualizar post:', errors)
        toast.error('Erro ao atualizar post')
      }
    })
  }

  const handleBlogToggle = (blogId: number) => {
    setSelectedBlogs(prev => {
      const newSelection = prev.includes(blogId) 
        ? prev.filter(id => id !== blogId)
        : [...prev, blogId]
      
      // Atualizar o blog_id principal (primeiro selecionado)
      if (newSelection.length > 0) {
        setData('blog_id', newSelection[0])
      } else {
        setData('blog_id', '')
      }
      
      return newSelection
    })
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'published':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">Publicado</span>
      case 'scheduled':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">Agendado</span>
      case 'draft':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">Rascunho</span>
      default:
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">Desconhecido</span>
    }
  }

  return (
    <DashboardLayout>
      <Head title="Editar Postagem" />
      
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Editar Postagem</h1>
            <p className="text-muted-foreground">Edite os dados da postagem</p>
          </div>
          <div className="flex items-center gap-2">
            {getStatusBadge(post.status)}
            <Button 
              variant="outline" 
              onClick={() => router.visit('/dashboard/posts')}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Voltar
            </Button>
          </div>
        </div>

        {errors && errors.length > 0 && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-6">
              <ul className="list-disc list-inside text-red-600">
                {errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Coluna Principal */}
            <div className="lg:col-span-2 space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Conteúdo da Postagem</CardTitle>
                  <CardDescription>Informações principais do post</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="title">Título *</Label>
                    <Input
                      id="title"
                      value={data.title}
                      onChange={(e) => setData('title', e.target.value)}
                      placeholder="Digite o título da postagem"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="content">Conteúdo *</Label>
                    <Textarea
                      id="content"
                      value={data.content}
                      onChange={(e) => setData('content', e.target.value)}
                      placeholder="Digite o conteúdo da postagem (Markdown suportado)"
                      rows={12}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="url">URL do Post</Label>
                    <Input
                      id="url"
                      type="url"
                      value={data.url}
                      onChange={(e) => setData('url', e.target.value)}
                      placeholder="https://exemplo.com/meu-post"
                    />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Configurações</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="status">Status</Label>
                    <Select value={data.status} onValueChange={(value) => setData('status', value)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="draft">Rascunho</SelectItem>
                        <SelectItem value="scheduled">Agendado</SelectItem>
                        <SelectItem value="published">Publicado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {data.status === 'scheduled' && (
                    <div>
                      <Label htmlFor="scheduled_at">Data/Hora de Publicação</Label>
                      <Input
                        id="scheduled_at"
                        type="datetime-local"
                        value={data.scheduled_at}
                        onChange={(e) => setData('scheduled_at', e.target.value)}
                      />
                    </div>
                  )}

                  {data.status === 'published' && (
                    <div>
                      <Label htmlFor="published_in">Publicado em</Label>
                      <Input
                        id="published_in"
                        type="datetime-local"
                        value={data.published_in}
                        onChange={(e) => setData('published_in', e.target.value)}
                      />
                    </div>
                  )}

                  <div>
                    <Label htmlFor="category_id">Categoria *</Label>
                    <Select value={data.category_id.toString()} onValueChange={(value) => setData('category_id', parseInt(value))}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione uma categoria" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((category) => (
                          <SelectItem key={category.id} value={category.id.toString()}>
                            {category.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Blogs de Destino</CardTitle>
                  <CardDescription>Selecione os blogs onde publicar</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {blogs.map((blog) => (
                      <div key={blog.id} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id={`blog-${blog.id}`}
                          checked={selectedBlogs.includes(blog.id)}
                          onChange={() => handleBlogToggle(blog.id)}
                          className="rounded border-gray-300"
                        />
                        <Label htmlFor={`blog-${blog.id}`} className="flex-1">
                          <div>
                            <div className="font-medium">{blog.name}</div>
                            <div className="text-sm text-muted-foreground">{blog.domain}</div>
                          </div>
                        </Label>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <div className="flex flex-col gap-2">
                <Button type="submit" disabled={processing} className="w-full">
                  <Save className="mr-2 h-4 w-4" />
                  {processing ? 'Salvando...' : 'Salvar Alterações'}
                </Button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </DashboardLayout>
  )
}
